import { Component } from '@theme/component';

/**
 * NKUK-1044: Desktop mega menu for the new navigation (see `sections/nk-header.liquid`).
 * Hover timings follow the test spec.
 */
const OPEN_DELAY = 150;
const SWITCH_DELAY = 60;
const AIM_DELAY = 250;

const FOCUSABLE = 'a[href], button:not([disabled])';

/**
 * @typedef {object} Refs
 * @property {HTMLElement} actionsSlot - Where the current header's <header-actions> is moved to.
 * @property {HTMLElement} panels - Container for every mega-menu panel.
 *
 * @extends {Component<Refs>}
 */
class NkHeader extends Component {
  requiredRefs = ['actionsSlot', 'panels'];

  #abortController = new AbortController();

  /** @type {string | null} Handle of the open primary item. */
  #openItem = null;

  /** @type {number | undefined} */
  #openTimer;

  /** @type {number | undefined} */
  #aimTimer;

  /** @type {HTMLElement | null} Level 2 item waiting for menu aim to settle. */
  #pendingL2 = null;

  /** @type {{ x: number, y: number }[]} Last two pointer positions, for menu aim. */
  #pointerTrail = [];

  connectedCallback() {
    super.connectedCallback();

    // A fresh controller, so listeners return if the element is re-attached.
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener('pointerover', this.#onPointerOver, { signal });
    this.addEventListener('pointerout', this.#onPointerOut, { signal });
    this.addEventListener('pointerleave', this.close, { signal });
    this.addEventListener('pointermove', this.#onPointerMove, { signal, passive: true });
    this.addEventListener('focusin', this.#onFocusIn, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });
    this.addEventListener('keydown', this.#onKeyDown, { signal });
    window.addEventListener('scroll', this.#onScroll, { signal, passive: true });
    document.getElementById('search-modal')?.addEventListener('dialog:open', this.#focusSearch, { signal });

    this.#onScroll();
    this.#placeActions();

    // The theme re-renders the current header after load, which puts its actions back.
    const currentHeader = document.getElementById('header-component');
    if (currentHeader) this.#actionsObserver.observe(currentHeader, { childList: true, subtree: true });
  }

  /**
   * Puts the cursor straight into the search field when the search pop-up opens.
   * @param {Event} event
   */
  #focusSearch = (event) => {
    if (!(event.currentTarget instanceof HTMLElement)) return;
    const input = event.currentTarget.querySelector('input[type="search"], input[name="q"]');
    if (input instanceof HTMLInputElement) input.focus();
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#actionsObserver.disconnect();
    clearTimeout(this.#openTimer);
    clearTimeout(this.#aimTimer);
  }

  /**
   * Opens a primary item's panel and turns the header white.
   * @param {string} handle
   */
  open(handle) {
    if (this.#openItem === handle) return;

    this.#closePanel();

    const panel = this.#panel(handle);
    if (!panel) return;

    this.style.setProperty('--nk-panel-top', `${this.refs.panels.getBoundingClientRect().top}px`);
    this.#measureOpenWidth();
    panel.hidden = false;
    this.#primaryLink(handle)?.setAttribute('aria-expanded', 'true');
    this.#openItem = handle;
    this.setAttribute('data-open', '');
  }

  /**
   * Widens the open header and panel if the primary items run past the panel width.
   * The panel's content keeps its size.
   */
  #measureOpenWidth() {
    const primary = this.querySelector('.nk-header__primary');
    if (!primary) return;

    // Leaves one item gap after the last item.
    const margin = parseFloat(getComputedStyle(primary).columnGap) || 0;
    const itemsEnd = primary.getBoundingClientRect().right - this.getBoundingClientRect().left;
    this.style.setProperty('--nk-open-width', `${Math.ceil(itemsEnd + margin)}px`);
  }

  /** Closes any open panel and fades the header back. */
  close = () => {
    clearTimeout(this.#openTimer);
    this.#closePanel();
    this.removeAttribute('data-open');
  };

  #closePanel() {
    if (!this.#openItem) return;

    const panel = this.#panel(this.#openItem);
    if (panel) {
      this.#closeL3(panel);
      panel.hidden = true;
    }
    this.#primaryLink(this.#openItem)?.setAttribute('aria-expanded', 'false');
    this.#openItem = null;
  }

  /**
   * Opens the level 3 column for a level 2 item. The column takes tile 1's slot, and the
   * item's own tile (if set) takes tile 2's slot.
   * @param {HTMLElement} l2Link
   */
  #openL3(l2Link) {
    const panel = /** @type {HTMLElement | null} */ (l2Link.closest('[data-panel]'));
    const handle = l2Link.dataset.l2;
    if (!panel || !handle) return;

    if (l2Link.getAttribute('aria-expanded') === 'true') return;

    this.#closeL3(panel);

    const column = panel.querySelector(`[data-l3="${handle}"]`);
    if (!(column instanceof HTMLElement)) return;

    column.hidden = false;
    l2Link.setAttribute('aria-expanded', 'true');
    panel.setAttribute('data-l3-open', handle);

    const subtile = panel.querySelector(`[data-subtile="${handle}"]`);
    panel.querySelectorAll('[data-default-tile]').forEach((tile, index) => {
      // Tile 1 always gives way to the column; tile 2 only when there's a sub-item tile.
      if (tile instanceof HTMLElement) tile.hidden = index === 0 || Boolean(subtile);
    });
    if (subtile instanceof HTMLElement) subtile.hidden = false;
  }

  /** @param {HTMLElement} panel */
  #closeL3(panel) {
    clearTimeout(this.#aimTimer);
    this.#pendingL2 = null;

    if (!panel.hasAttribute('data-l3-open')) return;

    panel.removeAttribute('data-l3-open');
    panel.querySelectorAll('[data-l3], [data-subtile]').forEach((element) => {
      if (element instanceof HTMLElement) element.hidden = true;
    });
    panel.querySelectorAll('[data-default-tile]').forEach((tile) => {
      if (tile instanceof HTMLElement) tile.hidden = false;
    });
    panel.querySelectorAll('[data-l2][aria-expanded]').forEach((link) => link.setAttribute('aria-expanded', 'false'));
  }

  /**
   * Shows the hovered level 2 item's column, or closes the open one if it has no children.
   * @param {HTMLElement} l2Link
   */
  #activateL2(l2Link) {
    if (l2Link.dataset.l2) {
      this.#openL3(l2Link);
    } else {
      const panel = /** @type {HTMLElement | null} */ (l2Link.closest('[data-panel]'));
      if (panel) this.#closeL3(panel);
    }
  }

  /** @param {PointerEvent} event */
  #onPointerOver = (event) => {
    if (event.pointerType === 'touch' || !(event.target instanceof Element)) return;

    const primary = /** @type {HTMLElement | null} */ (event.target.closest('[data-primary]'));
    if (primary) {
      clearTimeout(this.#openTimer);

      if (primary.hasAttribute('data-direct')) {
        this.close();
        return;
      }

      const handle = primary.dataset.primary;
      if (!handle || handle === this.#openItem) return;

      this.#openTimer = setTimeout(() => this.open(handle), this.#openItem ? SWITCH_DELAY : OPEN_DELAY);
      return;
    }

    const l2Link = /** @type {HTMLElement | null} */ (event.target.closest('[data-l2]'));
    if (!l2Link) return;

    if (this.#isAimingAtL3(l2Link)) {
      this.#pendingL2 = l2Link;
      clearTimeout(this.#aimTimer);
      this.#aimTimer = setTimeout(this.#checkAim, AIM_DELAY);
    } else {
      this.#pendingL2 = null;
      this.#activateL2(l2Link);
    }
  };

  /** @param {PointerEvent} event */
  #onPointerOut = (event) => {
    if (!(event.target instanceof Element)) return;

    // Leaving a primary item before its delay has run cancels it.
    const primary = event.target.closest('[data-primary]');
    if (primary && !primary.contains(/** @type {Node | null} */ (event.relatedTarget))) {
      clearTimeout(this.#openTimer);
    }
  };

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    this.#pointerTrail.push({ x: event.clientX, y: event.clientY });
    if (this.#pointerTrail.length > 2) this.#pointerTrail.shift();
  };

  /**
   * Menu aim: true while a level 3 column is open and the pointer is heading right towards it,
   * so passing over other level 2 items on the way doesn't switch the column.
   * @param {HTMLElement} l2Link
   */
  #isAimingAtL3(l2Link) {
    const panel = l2Link.closest('[data-panel]');
    if (!panel?.hasAttribute('data-l3-open')) return false;

    const [previous, current] = this.#pointerTrail;
    if (!previous || !current) return false;

    const dx = current.x - previous.x;
    const dy = current.y - previous.y;
    return dx > 0 && Math.abs(dx) >= Math.abs(dy);
  }

  /** Re-checks menu aim until the pointer stops or turns, then switches to the waiting item. */
  #checkAim = () => {
    const l2Link = this.#pendingL2;
    if (!l2Link) return;

    const trail = this.#pointerTrail.map((point) => `${point.x},${point.y}`).join();
    const moved = trail !== this.#lastAimTrail;
    this.#lastAimTrail = trail;

    if (moved && this.#isAimingAtL3(l2Link)) {
      this.#aimTimer = setTimeout(this.#checkAim, AIM_DELAY);
      return;
    }

    this.#pendingL2 = null;
    if (l2Link.matches(':hover')) this.#activateL2(l2Link);
  };

  /** @type {string} */
  #lastAimTrail = '';

  /** @param {FocusEvent} event */
  #onFocusIn = (event) => {
    if (!(event.target instanceof HTMLElement)) return;

    // Level 3 opens on keyboard focus as well as hover.
    const l2Link = /** @type {HTMLElement | null} */ (event.target.closest('[data-l2]'));
    if (l2Link) this.#activateL2(l2Link);
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    const next = /** @type {Node | null} */ (event.relatedTarget);
    if (next && this.contains(next)) return;
    if (this.matches(':hover')) return;
    this.close();
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    const target = event.target;

    if (event.key === 'Escape' && this.#openItem) {
      const link = this.#primaryLink(this.#openItem);
      this.close();
      link?.focus();
      return;
    }

    const primary = /** @type {HTMLElement | null} */ (target.closest('[data-primary]'));
    if (primary?.dataset.primary) {
      this.#onPrimaryKeyDown(event, primary.dataset.primary);
      return;
    }

    if (event.key === 'Tab' && this.#openItem) this.#onPanelTab(event, target);
  };

  /**
   * Enter or Space opens a closed item; Enter on an open item follows its link.
   * Tab from an open item moves into its panel.
   * @param {KeyboardEvent} event
   * @param {string} handle
   */
  #onPrimaryKeyDown(event, handle) {
    const isOpen = this.#openItem === handle;

    if ((event.key === 'Enter' && !isOpen) || event.key === ' ') {
      event.preventDefault();
      // Some apps (e.g. Instafeed) click the focused element on Enter even when it's cancelled.
      event.stopPropagation();
      if (isOpen) {
        this.close();
      } else {
        this.open(handle);
      }
      return;
    }

    if (event.key === 'Tab' && !event.shiftKey && isOpen) {
      const first = this.#focusables(this.#panel(handle))[0];
      if (first) {
        event.preventDefault();
        first.focus();
      }
    } else if (event.key === 'Tab' && this.#openItem) {
      this.close();
    }
  }

  /**
   * Keeps Tab in reading order inside an open panel, and closes the panel when focus moves
   * past either end of it.
   * @param {KeyboardEvent} event
   * @param {HTMLElement} target
   */
  #onPanelTab(event, target) {
    const handle = this.#openItem;
    const panel = handle ? this.#panel(handle) : null;
    if (!handle || !panel?.contains(target)) return;

    const focusables = this.#focusables(panel);
    const index = focusables.indexOf(target);
    const nextIndex = index + (event.shiftKey ? -1 : 1);
    const primaryLink = this.#primaryLink(handle);

    if (nextIndex < 0) {
      event.preventDefault();
      primaryLink?.focus();
      return;
    }

    if (nextIndex >= focusables.length) {
      event.preventDefault();
      const nextItem = primaryLink?.closest('li')?.nextElementSibling?.querySelector('a');
      this.close();
      (nextItem instanceof HTMLElement ? nextItem : primaryLink)?.focus();
    }
  }

  /** @param {HTMLElement | null} root */
  #focusables(root) {
    if (!root) return [];
    return [...root.querySelectorAll(FOCUSABLE)].filter(
      /** @returns {element is HTMLElement} */
      (element) => element instanceof HTMLElement && !element.closest('[hidden]')
    );
  }

  /** @param {string} handle */
  #panel(handle) {
    return /** @type {HTMLElement | null} */ (this.refs.panels.querySelector(`#nk-panel-${CSS.escape(handle)}`));
  }

  /** @param {string} handle */
  #primaryLink(handle) {
    return /** @type {HTMLElement | null} */ (this.querySelector(`[data-primary="${CSS.escape(handle)}"]`));
  }

  #onScroll = () => {
    this.toggleAttribute('data-scrolled', window.scrollY > 0);
  };

  /**
   * Moves the current header's <header-actions> (wishlist, account, cart) in here, so its
   * drawers exist only once.
   */
  #placeActions = () => {
    const actions = document.querySelector('#header-component header-actions');
    if (actions) this.refs.actionsSlot.replaceChildren(actions);
  };

  #actionsObserver = new MutationObserver(this.#placeActions);
}

if (!customElements.get('nk-header')) {
  customElements.define('nk-header', NkHeader);
}
